"use client";

import React, { useEffect, useRef } from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import './video-player.css';

// Only load quality levels if not already present to avoid warnings
if (typeof window !== 'undefined' && !videojs.getPlugin('qualityLevels')) {
  require('videojs-contrib-quality-levels');
}

// ── SVG Icons ────────────────────────────────────────────────────
const ICONS = {
  previous:
    '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 6h2v12H6zm3.5 6 8.5 6V6z"/></svg>',
  skipBack:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M11 17l-5-5 5-5"/><path d="M18 17l-5-5 5-5"/><text x="5.5" y="22" font-size="0" fill="currentColor">10</text></svg>',
  play:
    '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>',
  pause:
    '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>',
  next:
    '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 18l8.5-6L6 6v12zm10-12v12h2V6z"/></svg>',
  volumeHigh:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/></svg>',
  volumeMute:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="1" x2="1" y2="23"/></svg>',
  waveform:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M2 12h1.5M5 8v8M8 5v14M11 9v6M14 6v12M17 8v8M20 10v4M22 12h-1.5"/></svg>',
  stop:
    '<svg viewBox="0 0 24 24" fill="currentColor"><rect x="5" y="5" width="14" height="14" rx="2.5"/></svg>',
  settings:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-2.82 1.17V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>',
  fullscreen:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/></svg>',
  exitFullscreen:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"/></svg>',
  check:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>',
  chevronDown:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>',
};

const SKIP_BACK_HTML = `
  <span class="vjs-icon-placeholder">
    <span style="display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1px;color:rgba(255,255,255,0.85)">
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
        <path d="M3 3v5h5"/>
      </svg>
      <span style="font-size:7px;font-weight:700;color:white;line-height:1">10</span>
    </span>
  </span>`;

const SKIP_FWD_HTML = `
  <span class="vjs-icon-placeholder">
    <span style="display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1px;color:rgba(255,255,255,0.85)">
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/>
        <path d="M21 3v5h-5"/>
      </svg>
      <span style="font-size:7px;font-weight:700;color:white;line-height:1">10</span>
    </span>
  </span>`;

// Formatage propre du temps
function formatTime(seconds: number) {
  if (isNaN(seconds) || seconds < 0 || seconds === Infinity) return '0:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) {
    return `${h}:${m < 10 ? '0' + m : m}:${s < 10 ? '0' + s : s}`;
  }
  return `${m}:${s < 10 ? '0' + s : s}`;
}

// ── vjs component base classes ───────────────────────────────────
const Component = videojs.getComponent('Component');
const Button    = videojs.getComponent('Button');

class ProgressRow       extends Component { createEl() { return videojs.dom.createEl('div', { className: 'vjs-progress-row' }); } }
class ControlsContainer extends Component { createEl() { return videojs.dom.createEl('div', { className: 'vjs-cinematic-controls-container' }); } }
class LeftControls      extends Component { createEl() { return videojs.dom.createEl('div', { className: 'vjs-cinematic-left' }); } }
class CenterControls    extends Component { createEl() { return videojs.dom.createEl('div', { className: 'vjs-cinematic-center' }); } }
class RightControls     extends Component { createEl() { return videojs.dom.createEl('div', { className: 'vjs-cinematic-right' }); } }

// ── Composant Custom: Toggle Temps Restant / Durée ───────────────
class TimeToggleDisplay extends Component {
  private showDuration = false;

  constructor(player: any, options: any) {
    super(player, options);
    this.on(player, 'timeupdate', () => this.updateContent());
    this.on(player, 'durationchange', () => this.updateContent());
    this.on('click', () => this.handleClick());
  }

  createEl() {
    const el = super.createEl('div', { 
      className: 'vjs-time-toggle-display vjs-remaining-time' 
    });
    el.innerHTML = `<span class="vjs-time-text">0:00</span>`;
    el.title = 'Basculer temps/durée';
    return el;
  }

  handleClick() {
    this.showDuration = !this.showDuration;
    this.updateContent();
  }

  updateContent() {
    const timeText = this.el().querySelector('.vjs-time-text');
    if (!timeText) return;
    const p = this.player() as any;
    // VHS reports Infinity for event playlists without #EXT-X-ENDLIST — use stored ffprobe value
    const raw = p.duration();
    const duration = (isFinite(raw) ? raw : p.__totalDuration) || 0;
    const current = p.currentTime() || 0;

    if (this.showDuration) {
      timeText.textContent = formatTime(duration);
    } else {
      const remaining = duration - current;
      timeText.textContent = '-' + formatTime(remaining > 0 ? remaining : 0);
    }
  }
}

class PreviousButton extends Button {
  createEl() {
    const el = super.createEl('button', { className: 'vjs-previous-control vjs-control vjs-button' });
    el.innerHTML = `<span class="vjs-icon-placeholder">${ICONS.previous}</span>`;
    el.title = 'Titre précédent';
    return el;
  }
  handleClick() {
    const p = this.player() as any;
    if ((p.currentTime() as number) > 3) {
      p.currentTime(0);
    } else {
      p.__osmose?.onPrev?.();
    }
  }
}

class SkipBackwardButton extends Button {
  createEl() {
    const el = super.createEl('button', { className: 'vjs-skip-backward vjs-control vjs-button' });
    el.innerHTML = SKIP_BACK_HTML;
    el.title = 'Reculer de 10s';
    return el;
  }
  handleClick() { const p = this.player(); p.currentTime(Math.max(0, (p.currentTime() as number) - 10)); }
}

class SkipForwardButton extends Button {
  createEl() {
    const el = super.createEl('button', { className: 'vjs-skip-forward vjs-control vjs-button' });
    el.innerHTML = SKIP_FWD_HTML;
    el.title = 'Avancer de 10s';
    return el;
  }
  handleClick() { const p = this.player(); p.currentTime(Math.min((p.duration() as number), (p.currentTime() as number) + 10)); }
}

class NextButton extends Button {
  createEl() {
    const el = super.createEl('button', { className: 'vjs-next-control vjs-control vjs-button' });
    el.innerHTML = `<span class="vjs-icon-placeholder">${ICONS.next}</span>`;
    el.title = 'Titre suivant';
    return el;
  }
  handleClick() { (this.player() as any).__osmose?.onNext?.(); }
}

class CloseButton extends Button {
  createEl() {
    const el = super.createEl('button', { className: 'vjs-close-control vjs-control vjs-button' });
    el.innerHTML = `<span class="vjs-icon-placeholder">${ICONS.stop}</span>`;
    el.title = 'Fermer le lecteur';
    return el;
  }
  handleClick() { (this.player() as any).__osmose?.onClose?.(); }
}

// ── Audio / Subtitles modal ───────────────────────────────────────
class AudioTrackButton extends Button {
  private modal: HTMLElement | null = null;
  createEl() {
    const el = super.createEl('button', { className: 'vjs-audiotrack-control vjs-control vjs-button' });
    el.innerHTML = `<span class="vjs-icon-placeholder">${ICONS.waveform}</span>`;
    el.title = 'Audio & Sous-titres';
    return el;
  }

  handleClick() {
    const existing = this.player().el().querySelector('.modal-audio');
    if (existing) { existing.remove(); this.modal = null; return; }
    this.player().el().querySelectorAll('.vjs-cinematic-modal').forEach(m => m.remove());

    const p = this.player() as any;
    const audioTracks: any[] = Array.from(p.audioTracks?.() ?? []);
    const allRemote: any[] = Array.from(p.remoteTextTracks?.() ?? []);
    // Read subtitle tracks from remoteTextTracks (where addRemoteTextTrack puts them)
    // and filter to only subtitle/caption kind (ignore VHS metadata tracks)
    const subTracks: any[] = allRemote
      .filter((t: any) => t.kind === 'subtitles' || t.kind === 'captions');
    // Temporary debug line — remove after confirming

    const modal = document.createElement('div');
    modal.className = 'vjs-cinematic-modal modal-audio';

    const audioRows = audioTracks.length
      ? audioTracks.map((t, i) => `
          <div class="vjs-modal-row" data-audio="${i}">
            <span class="vjs-modal-row-label">${t.label || t.language || `Piste ${i + 1}`}</span>
            <span class="vjs-modal-row-value no-arrow">
              <span class="vjs-modal-check ${t.enabled ? 'active' : ''}">${ICONS.check}</span>
            </span>
          </div>`).join('')
      : `<div class="vjs-modal-row"><span class="vjs-modal-row-label" style="color:rgba(255,255,255,0.35)">Aucune piste audio</span></div>`;

    const subRows = subTracks.length
      ? [`<div class="vjs-modal-row" data-sub="-1">
            <span class="vjs-modal-row-label">Désactivés</span>
            <span class="vjs-modal-row-value no-arrow">
              <span class="vjs-modal-check" id="sub-off-check">${ICONS.check}</span>
            </span>
          </div>`,
         ...subTracks.map((t, i) => `
          <div class="vjs-modal-row" data-sub="${i}">
            <span class="vjs-modal-row-label">${t.label || t.language || `Sous-titres ${i + 1}`}</span>
            <span class="vjs-modal-row-value no-arrow">
              <span class="vjs-modal-check ${t.mode === 'showing' ? 'active' : ''}">${ICONS.check}</span>
            </span>
          </div>`)].join('')
      : `<div class="vjs-modal-row"><span class="vjs-modal-row-label" style="color:rgba(255,255,255,0.35)">Aucun sous-titre</span></div>`;

    modal.innerHTML = `
      <div class="vjs-modal-section">
        <div class="vjs-modal-section-title">Audio</div>
        ${audioRows}
      </div>
      <div class="vjs-modal-section">
        <div class="vjs-modal-section-title">Sous-titres</div>
        ${subRows}
      </div>`;

    modal.querySelectorAll('[data-audio]').forEach((row, i) => {
      row.addEventListener('click', () => {
        audioTracks.forEach((t, j) => { t.enabled = j === i; });
        modal.querySelectorAll('[data-audio] .vjs-modal-check').forEach((c, j) => {
          (c as HTMLElement).classList.toggle('active', j === i);
        });
      });
    });

    modal.querySelectorAll('[data-sub]').forEach(row => {
      row.addEventListener('click', () => {
        const idx = parseInt((row as HTMLElement).dataset.sub ?? '-1');
        subTracks.forEach((t, j) => { t.mode = j === idx ? 'showing' : 'hidden'; });
        modal.querySelectorAll('[data-sub] .vjs-modal-check').forEach((c, j) => {
          (c as HTMLElement).classList.toggle('active', j - 1 === idx);
        });
        const offCheck = modal.querySelector('#sub-off-check');
        if (offCheck) offCheck.classList.toggle('active', idx === -1);
      });
    });

    this.player().el().appendChild(modal);
    this.modal = modal;
    setTimeout(() => {
      const close = (e: MouseEvent) => {
        if (!modal.contains(e.target as Node) && !(this.el() as HTMLElement).contains(e.target as Node)) {
          modal.remove(); document.removeEventListener('click', close);
        }
      };
      document.addEventListener('click', close);
    }, 0);
  }
}

// ── Settings modal ────────────────────────────────────────────────
class SettingsButton extends Button {
  createEl() {
    const el = super.createEl('button', { className: 'vjs-settings-control vjs-control vjs-button' });
    el.innerHTML = `<span class="vjs-icon-placeholder">${ICONS.settings}</span>`;
    el.title = 'Paramètres';
    return el;
  }

  handleClick() {
    const existing = this.player().el().querySelector('.modal-settings');
    if (existing) { existing.remove(); return; }
    this.player().el().querySelectorAll('.vjs-cinematic-modal').forEach(m => m.remove());

    const p = this.player() as any;
    const qualityLevels: any[] = Array.from(p.qualityLevels?.() ?? []);
    const speeds = [0.5, 0.75, 1, 1.25, 1.5, 2];
    const currentSpeed = p.playbackRate?.() ?? 1;
    const subColors = ['#ffffff', '#ffff00', '#00ff00', '#00cfff', '#ff6060'];

    const qualityHtml = qualityLevels.length
      ? `<div class="vjs-modal-qualities">${qualityLevels.map((q, i) => `
          <div class="vjs-modal-quality-pill ${q.enabled ? 'active' : ''}" data-quality="${i}">
            ${q.height ? q.height + 'p' : q.label ?? `Q${i}`}
          </div>`).join('')}
          <div class="vjs-modal-quality-pill active" data-quality="auto">Auto</div>
        </div>`
      : `<div class="vjs-modal-qualities"><div class="vjs-modal-quality-pill active" data-quality="auto">Auto</div></div>`;

    const modal = document.createElement('div');
    modal.className = 'vjs-cinematic-modal modal-settings';
    modal.innerHTML = `
      <div class="vjs-modal-section">
        <div class="vjs-modal-section-title">Qualité</div>
        ${qualityHtml}
      </div>
      <div class="vjs-modal-section">
        <div class="vjs-modal-section-title">Vitesse</div>
        <div class="vjs-modal-speeds">
          ${speeds.map(s => `
            <div class="vjs-modal-speed-pill ${s === currentSpeed ? 'active' : ''}" data-speed="${s}">
              ${s === 1 ? 'Normale' : s + '×'}
            </div>`).join('')}
        </div>
      </div>
      <div class="vjs-modal-section">
        <div class="vjs-modal-section-title">Sous-titres</div>
        <div class="vjs-modal-section-title" style="padding-top:2px;padding-bottom:6px;font-size:10px">Couleur</div>
        <div class="vjs-modal-swatches">
          ${subColors.map((c, i) => `
            <div class="vjs-modal-swatch ${i === 0 ? 'active' : ''}"
                 data-color="${c}" style="background:${c}"></div>`).join('')}
        </div>
        <div class="vjs-modal-slider-row">
          <div class="vjs-modal-slider-label">Taille <span id="sub-size-val">100%</span></div>
          <input type="range" class="vjs-modal-slider" id="sub-size" min="50" max="200" value="100" step="5">
        </div>
        <div class="vjs-modal-slider-row">
          <div class="vjs-modal-slider-label">Décalage <span id="sub-offset-val">0.0s</span></div>
          <input type="range" class="vjs-modal-slider" id="sub-offset" min="-10" max="10" value="0" step="0.1">
        </div>
      </div>`;

    modal.querySelectorAll('[data-speed]').forEach(pill => {
      pill.addEventListener('click', () => {
        const speed = parseFloat((pill as HTMLElement).dataset.speed ?? '1');
        p.playbackRate(speed);
        modal.querySelectorAll('[data-speed]').forEach(pp => pp.classList.remove('active'));
        pill.classList.add('active');
      });
    });

    modal.querySelectorAll('[data-quality]').forEach(pill => {
      pill.addEventListener('click', () => {
        const idx = (pill as HTMLElement).dataset.quality;
        if (idx !== 'auto' && p.qualityLevels) {
          Array.from(p.qualityLevels()).forEach((q: any, j: number) => { q.enabled = j === parseInt(idx!); });
        }
        modal.querySelectorAll('[data-quality]').forEach(pp => pp.classList.remove('active'));
        pill.classList.add('active');
      });
    });

    modal.querySelectorAll('[data-color]').forEach(swatch => {
      swatch.addEventListener('click', () => {
        modal.querySelectorAll('[data-color]').forEach(s => s.classList.remove('active'));
        swatch.classList.add('active');
        const display = p.el().querySelector('.vjs-text-track-display') as HTMLElement | null;
        if (display) display.style.color = (swatch as HTMLElement).dataset.color ?? '#fff';
      });
    });

    const sizeSlider = modal.querySelector('#sub-size') as HTMLInputElement;
    const sizeVal    = modal.querySelector('#sub-size-val') as HTMLElement;
    sizeSlider?.addEventListener('input', () => {
      sizeVal.textContent = sizeSlider.value + '%';
      const display = p.el().querySelector('.vjs-text-track-display') as HTMLElement | null;
      if (display) display.style.fontSize = (parseInt(sizeSlider.value) / 100) + 'em';
    });

    const offsetSlider = modal.querySelector('#sub-offset') as HTMLInputElement;
    const offsetVal    = modal.querySelector('#sub-offset-val') as HTMLElement;
    let subOffset = 0;
    offsetSlider?.addEventListener('input', () => {
      const newOffset = parseFloat(offsetSlider.value);
      const delta = newOffset - subOffset;
      subOffset = newOffset;
      offsetVal.textContent = newOffset.toFixed(1) + 's';
      Array.from(p.remoteTextTracks?.() ?? []).forEach((track: any) => {
        if (track.mode === 'showing' && track.cues) {
          Array.from(track.cues).forEach((cue: any) => {
            cue.startTime += delta;
            cue.endTime   += delta;
          });
        }
      });
    });

    this.player().el().appendChild(modal);
    setTimeout(() => {
      const close = (e: MouseEvent) => {
        if (!modal.contains(e.target as Node) && !(this.el() as HTMLElement).contains(e.target as Node)) {
          modal.remove(); document.removeEventListener('click', close);
        }
      };
      document.addEventListener('click', close);
    }, 0);
  }
}

// ── Fullscreen button ─────────────────────────────────────────────
class FullscreenButton extends Button {
  createEl() {
    const el = super.createEl('button', { className: 'vjs-fullscreen-toggle vjs-control vjs-button' });
    el.innerHTML = `<span class="vjs-icon-placeholder">${ICONS.fullscreen}</span>`;
    el.title = 'Plein écran';
    return el;
  }
  handleClick() {
    const p = this.player();
    if (p.isFullscreen()) {
      p.exitFullscreen();
      this.el().innerHTML = `<span class="vjs-icon-placeholder">${ICONS.fullscreen}</span>`;
      (this.el() as HTMLElement).title = 'Plein écran';
    } else {
      p.requestFullscreen();
      this.el().innerHTML = `<span class="vjs-icon-placeholder">${ICONS.exitFullscreen}</span>`;
      (this.el() as HTMLElement).title = 'Quitter le plein écran';
    }
  }
}

// ── Register ─────────────────────────────────────────────────────
// Each registration is wrapped individually so a duplicate error from Next.js HMR
// on one component doesn't block the others from registering.
const reg = (name: string, component: any) => {
  try { videojs.registerComponent(name, component); } catch { /* already registered */ }
};
reg('ProgressRow',        ProgressRow);
reg('ControlsContainer',  ControlsContainer);
reg('LeftControls',       LeftControls);
reg('CenterControls',     CenterControls);
reg('RightControls',      RightControls);
reg('PreviousButton',     PreviousButton);
reg('SkipBackwardButton', SkipBackwardButton);
reg('SkipForwardButton',  SkipForwardButton);
reg('NextButton',         NextButton);
reg('CloseButton',        CloseButton);
reg('TimeToggleDisplay',  TimeToggleDisplay);
reg('AudioTrackButton',   AudioTrackButton);
reg('SettingsButton',     SettingsButton);
reg('FullscreenButton',   FullscreenButton);

// ── Helpers ───────────────────────────────────────────────────────

function normSrc(src: string): string {
  try { return new URL(src, window.location.href).pathname; } catch { return src; }
}

function applySubtitleTracks(player: any, tracks: SubtitleTrack[]) {
  const existing = Array.from(player.remoteTextTracks() as any[])
    .filter((t: any) => t.kind === 'subtitles' || t.kind === 'captions');

  const wantedPaths = new Set(tracks.map(t => normSrc(t.url)));

  // Remove tracks no longer wanted
  existing.forEach((t: any) => {
    if (!wantedPaths.has(normSrc(t.src ?? ''))) {
      try { player.removeRemoteTextTrack(t); } catch { /* ignore */ }
    }
  });

  const existingPaths = new Set(existing.map((t: any) => normSrc(t.src ?? '')));

  // Add tracks not already present — manualCleanup=true so VHS won't auto-remove them
  tracks.forEach(track => {
    if (!existingPaths.has(normSrc(track.url))) {
      player.addRemoteTextTrack({
        kind: 'subtitles',
        src: track.url,
        srclang: track.lang,
        label: track.label,
        default: false
      }, true);
    }
  });
}

// ── Props ─────────────────────────────────────────────────────────
interface SubtitleTrack {
  lang: string;
  label: string;
  url: string;
}

interface VideoPlayerProps {
  options?: Record<string, any>;
  onReady?: (player: any) => void;
  title?: string;
  subtitle?: string;
  artwork?: string;
  mode?: 'mini' | 'large';
  onModeChange?: (mode: 'mini' | 'large') => void;
  subtitleTracks?: SubtitleTrack[];
  onNext?: () => void;
  onPrev?: () => void;
  hasNext?: boolean;
  hasPrev?: boolean;
  onProgress?: (position: number, duration: number) => void;
  startPosition?: number | null;
  onClose?: () => void;
}

export const VideoPlayer = ({
  options = {}, onReady, title, subtitle, artwork,
  mode = 'large', onModeChange,
  subtitleTracks = [], onNext, onPrev, hasNext, hasPrev,
  onProgress, startPosition, onClose
}: VideoPlayerProps) => {
  const videoRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  const startPositionRef = useRef<number | null>(null);
  const subtitleTracksRef = useRef<SubtitleTrack[]>(subtitleTracks);
  // totalDurationRef: ffprobe value read by the init-time durationchange listener (avoids stale closure)
  const totalDurationRef = useRef<number | null>(null);

  useEffect(() => {
    startPositionRef.current = startPosition ?? null;
  }, [startPosition]);

  // Update ref AND apply to player if already alive (handles media switches)
  useEffect(() => {
    subtitleTracksRef.current = subtitleTracks;
    const player = playerRef.current;
    if (player) applySubtitleTracks(player, subtitleTracks);
  }, [subtitleTracks]);

  // When source changes: load new source, seek to saved position if any
  useEffect(() => {
    if (playerRef.current && options.sources) {
      playerRef.current.src(options.sources);
      playerRef.current.currentTime(0);

      const pos = startPositionRef.current;
      if (pos && pos > 0) {
        playerRef.current.one('loadedmetadata', () => {
          if (playerRef.current) playerRef.current.currentTime(pos);
        });
      }
    }
  }, [options.sources]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync next/prev/close callbacks onto the player instance so vjs buttons can call them
  useEffect(() => {
    if (playerRef.current) {
      playerRef.current.__osmose = {
        onNext: hasNext ? onNext : undefined,
        onPrev: hasPrev ? onPrev : undefined,
        onClose,
      };
    }
  }, [onNext, onPrev, hasNext, hasPrev, onClose]);

  useEffect(() => {
    if (playerRef.current) {
      playerRef.current.__onProgress = onProgress;
    }
  }, [onProgress]);

  // Update title/subtitle overlay when they change
  useEffect(() => {
    if (playerRef.current) {
      const overlay = playerRef.current.el().querySelector('.vjs-cinematic-title');
      if (overlay) {
        overlay.innerHTML = `
          <h2>${title || ''}</h2>
          ${subtitle ? `<div class="subtitle">${subtitle}</div>` : ''}`;
      }
      const miniTitle = playerRef.current.el().querySelector('.vjs-mini-title');
      if (miniTitle) {
        miniTitle.innerHTML = `
          <div class="vjs-mini-title-main">${title || ''}</div>
          ${subtitle ? `<div class="vjs-mini-title-sub">${subtitle}</div>` : ''}
        `;
      }
    }
  }, [title, subtitle]);

  // Keep totalDurationRef in sync with the ffprobe value from the backend.
  // The actual listener is registered in the init callback (player doesn't exist yet here).
  useEffect(() => {
    const dur = parseFloat(String(options.duration));
    const valid = !isNaN(dur) && dur > 0 ? dur : null;
    totalDurationRef.current = valid;
    const p = playerRef.current;
    if (p && valid) {
      p.__totalDuration = valid;
      // If VHS has already set Infinity (event playlist without #EXT-X-ENDLIST), override now
      if (!isFinite(p.duration())) p.duration(valid);
    }
  }, [options.duration]);

  useEffect(() => {
    if (!playerRef.current && videoRef.current) {
      const videoElement = document.createElement('video');
      videoElement.className = 'video-js vjs-cinematic';
      videoElement.setAttribute('webkit-playsinline', 'true');
      videoElement.setAttribute('playsinline', 'true');
      videoRef.current.appendChild(videoElement);

      const player = (playerRef.current = videojs(videoElement, {
        ...options,
        controls: true,
        inactivityTimeout: 3000,
        controlBar: {
          children: [
            {
              name: 'progressRow',
              children: ['currentTimeDisplay', 'progressControl', 'timeToggleDisplay'],
            },
            {
              name: 'controlsContainer',
              children: [
                {
                  name: 'leftControls',
                  children: [
                    'volumePanel',
                  ],
                },
                {
                  name: 'centerControls',
                  children: [
                    'previousButton',
                    'skipBackwardButton',
                    'playToggle',
                    'skipForwardButton',
                    'nextButton',
                    'closeButton',
                  ],
                },
                {
                  name: 'rightControls',
                  children: ['audioTrackButton', 'settingsButton', 'fullscreenButton'],
                },
              ],
            },
          ],
        },
      }, () => {
        
        // Custom Mini Video Anchor & Title
        const controlBar = player.el().querySelector('.vjs-control-bar');
        const controlsContainer = player.el().querySelector('.vjs-cinematic-controls-container');
        const leftControls = player.el().querySelector('.vjs-cinematic-left');
        
        const miniAnchor = document.createElement('div');
        miniAnchor.className = 'vjs-mini-video-anchor';
        
        const miniTitle = document.createElement('div');
        miniTitle.className = 'vjs-mini-title';
        if (title) {
          miniTitle.innerHTML = `
            <div class="vjs-mini-title-main">${title}</div>
            ${subtitle ? `<div class="vjs-mini-title-sub">${subtitle}</div>` : ''}
          `;
        }

        if (controlsContainer && leftControls) {
           controlsContainer.insertBefore(miniAnchor, leftControls);
           if (title) {
             controlsContainer.insertBefore(miniTitle, leftControls);
           }
        }

        // Restore play state after mode-change clicks so Video.js click handler can't pause
        const expandPlayer = (e: Event) => {
          e.stopImmediatePropagation();
          e.preventDefault();
          const wasPlaying = !player.paused();
          onModeChange?.('large');
          if (wasPlaying) setTimeout(() => { if (player.paused()) player.play(); }, 0);
        };

        miniAnchor.addEventListener('click', expandPlayer);

        // Title overlay (Top-Left)
        if (title) {
          const overlay = document.createElement('div');
          overlay.className = 'vjs-cinematic-title';
          overlay.innerHTML = `
            <h2>${title}</h2>
            ${subtitle ? `<div class="subtitle">${subtitle}</div>` : ''}`;
          player.el().appendChild(overlay);
        }

        // End Time Overlay (Top-Right)
        const endTimeEl = document.createElement('div');
        endTimeEl.className = 'vjs-cinematic-end-time';
        endTimeEl.style.display = 'none';
        player.el().appendChild(endTimeEl);

        const reduceBtn = document.createElement('button');
        reduceBtn.className = 'vjs-reduce-btn-standalone';
        reduceBtn.innerHTML = ICONS.chevronDown;
        reduceBtn.title = 'Réduire la vidéo';
        player.el().appendChild(reduceBtn);

        const updateEndTime = () => {
          const duration = player.duration() as number;
          const current = player.currentTime() as number;

          if (!duration || isNaN(duration) || duration === Infinity) {
            endTimeEl.style.display = 'none';
            return;
          }

          endTimeEl.style.display = 'flex';
          const remaining = duration - current;
          const now = new Date();
          now.setSeconds(now.getSeconds() + remaining);
          
          const hrs = String(now.getHours()).padStart(2, '0');
          const mins = String(now.getMinutes()).padStart(2, '0');
          endTimeEl.innerHTML = `Fin à <strong>${hrs}:${mins}</strong>`;
        };

        player.on('timeupdate', updateEndTime);
        player.on('durationchange', updateEndTime);

        reduceBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          const wasPlaying = !player.paused();
          if (player.isFullscreen()) player.exitFullscreen();
          onModeChange?.('mini');
          if (wasPlaying) setTimeout(() => { if (player.paused()) player.play(); }, 0);
        });

        // Artwork
        if (artwork !== undefined) {
          const art = document.createElement('div');
          art.className = 'vjs-cinematic-artwork';
          art.innerHTML = artwork
            ? `<img src="${artwork}" alt="${title ?? ''}" />`
            : `<div class="vjs-cinematic-artwork-placeholder">
                 <svg viewBox="0 0 24 24" fill="currentColor" width="60" height="60" opacity=".45">
                   <path d="M12 3v10.55A4 4 0 1 0 14 17V7h4V3h-6z"/>
                 </svg>
               </div>`;
          player.el().insertBefore(art, player.el().querySelector('.vjs-control-bar'));
        }

        // ── INJECTION MANUELLE DE NOS SVG DANS LES BOUTONS NATIFS ──
        const playBtn = player.el().querySelector('.vjs-play-control .vjs-icon-placeholder');
        if (playBtn) {
          const updatePlayIcon = () => { playBtn.innerHTML = player.paused() ? ICONS.play : ICONS.pause; };
          updatePlayIcon();
          player.on('play', updatePlayIcon);
          player.on('pause', updatePlayIcon);
        }

        const muteBtnIcon = player.el().querySelector('.vjs-mute-control .vjs-icon-placeholder');
        if (muteBtnIcon) {
          const updateMuteIcon = () => {
            const vol = player.volume();
            const muted = player.muted();
            muteBtnIcon.innerHTML = (muted || vol === 0) ? ICONS.volumeMute : ICONS.volumeHigh;
          };
          updateMuteIcon();
          player.on('volumechange', updateMuteIcon);
        }

        // ── Volume fill pill ──────────────────────────────────
        const updateVolumePill = () => {
          const panel  = player.el().querySelector('.vjs-volume-panel') as HTMLElement | null;
          const muteBtn = player.el().querySelector('.vjs-mute-control') as HTMLElement | null;
          if (!panel) return;
          const vol = player.muted() ? 0 : Math.round((player.volume() as number) * 100);
          panel.style.setProperty('--vol', `${vol}%`);
          if (muteBtn) muteBtn.style.color = vol > 22 ? '#1a1a1a' : '#ffffff';
        };

        player.on('volumechange', updateVolumePill);
        player.on('ready', updateVolumePill);
        updateVolumePill();

        // Drag-to-scrub on the volume pill
        const panel = player.el().querySelector('.vjs-volume-panel') as HTMLElement | null;
        if (panel) {
          let dragging = false;
          const scrubVol = (e: MouseEvent | TouchEvent) => {
            const rect   = panel.getBoundingClientRect();
            const clientX = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
            const pct    = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
            player.muted(false);
            player.volume(pct);
          };
          
          panel.addEventListener('mousedown',  (e) => { 
            if ((e.target as Element).closest('.vjs-mute-control')) return;
            dragging = true; scrubVol(e); 
          });
          panel.addEventListener('touchstart', (e) => { 
            if ((e.target as Element).closest('.vjs-mute-control')) return;
            dragging = true; scrubVol(e); 
          }, { passive: true });
          
          window.addEventListener('mousemove',  (e) => { if (dragging) scrubVol(e); });
          window.addEventListener('touchmove',  (e) => { if (dragging) scrubVol(e as any); }, { passive: true });
          window.addEventListener('mouseup',   () => { dragging = false; });
          window.addEventListener('touchend',  () => { dragging = false; });
        }

        player.on('fullscreenchange', () => {
          if (player.isFullscreen()) onModeChange?.('large');
        });

        // ── Progress reporting ────────────────────────────────
        (player as any).__osmose = { onNext: undefined, onPrev: undefined, onClose };
        (player as any).__onProgress = undefined;

        const reportNow = () => {
          const pos = player.currentTime() as number;
          const dur = player.duration() as number;
          if (pos > 0 && dur > 0) (player as any).__onProgress?.(pos, dur);
        };

        const progressInterval = setInterval(() => {
          if (!player.paused()) reportNow();
        }, 15000);

        // Save when the tab goes to background (user switches tab / minimises)
        const onVisibilityChange = () => { if (document.hidden) reportNow(); };
        // Save when the page is about to be unloaded (refresh, close, navigate)
        const onBeforeUnload = () => reportNow();

        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('beforeunload', onBeforeUnload);

        player.on('pause', reportNow);
        player.on('dispose', () => {
          clearInterval(progressInterval);
          document.removeEventListener('visibilitychange', onVisibilityChange);
          window.removeEventListener('beforeunload', onBeforeUnload);
          reportNow();
        });

        // ── Duration override ─────────────────────────────────
        // VHS sets Infinity for HLS event playlists without #EXT-X-ENDLIST (transcoding in progress).
        // We override with the ffprobe duration so the seekbar shows the full film length immediately.
        // Only fires when VHS sets Infinity — once VHS has ENDLIST it calculates the real value.
        const forceDuration = () => {
          const dur = totalDurationRef.current;
          if (dur && !isFinite(player.duration())) player.duration(dur);
        };
        player.on('durationchange', forceDuration);
        player.on('loadedmetadata', forceDuration);
        if (totalDurationRef.current) player.__totalDuration = totalDurationRef.current;

        // ── Subtitle tracks ───────────────────────────────────
        applySubtitleTracks(player, subtitleTracksRef.current);
        // Re-inject after each HLS segment load cycle — VHS with audio renditions fires
        // loadstart multiple times; loadedmetadata fires once after the manifest is parsed.
        const reInjectSubs = () => applySubtitleTracks(player, subtitleTracksRef.current);
        player.on('loadstart',    () => setTimeout(reInjectSubs, 0));
        player.on('loadedmetadata', reInjectSubs);

        onReady?.(player);
      }));
    }
  }, [options, title, subtitle, artwork, onModeChange]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (playerRef.current) {
      const el = playerRef.current.el() as HTMLElement;
      el.classList.toggle('vjs-mode-mini', mode === 'mini');
      el.classList.toggle('vjs-mode-large', mode === 'large');
      // Inline style required — CSS !important cannot override Video.js's own inline styles
      el.style.backgroundColor = mode === 'mini' ? 'transparent' : '';

      const tech = el.querySelector('.vjs-tech') as HTMLElement;
      if (tech) {
        if (mode === 'mini') {
          // Video.js sets width/height as inline styles; only inline style can beat inline style
          tech.style.width = '160px';
          tech.style.height = '90px';
          tech.style.position = 'absolute';
          tech.style.top = 'auto';
          tech.style.bottom = '16px';
          tech.style.left = '24px';
          tech.style.borderRadius = '12px';
          tech.style.objectFit = 'cover';
          tech.style.zIndex = '100';
          tech.onclick = (e: MouseEvent) => {
            e.stopImmediatePropagation();
            e.preventDefault();
            const p = playerRef.current;
            const wasPlaying = p && !p.paused();
            onModeChange?.('large');
            if (wasPlaying) setTimeout(() => { if (p && p.paused()) p.play(); }, 0);
          };
        } else {
          tech.style.width = '';
          tech.style.height = '';
          tech.style.position = '';
          tech.style.top = '';
          tech.style.bottom = '';
          tech.style.left = '';
          tech.style.borderRadius = '';
          tech.style.objectFit = '';
          tech.style.zIndex = '';
          tech.onclick = null;
        }
      }
    }
  }, [mode, onModeChange]);

  useEffect(() => {
    return () => {
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, []);

  return (
    <div data-vjs-player className="w-full h-full relative">
      <div ref={videoRef} className="w-full h-full" />
    </div>
  );
};

export default VideoPlayer;
